import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDoc,
  getDocs,
  doc,
} from "firebase/firestore";
import {
  Button,
  LinearProgress,
  Box,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import { db } from "../../firebase";
import axios from "axios";
import ReactMarkdown from "react-markdown";

function Ai(props) {
  const clientID = props.clientId;
  const [clientInfo, setClientInfo] = useState(null);
  const [clientNotes, setClientNotes] = useState([]);
  const [, setClientInfoTokens] = useState(0);
  const [, setClientNotesTokens] = useState(0);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [emergencyDischarge, setEmergencyDischarge] = useState(null);
  const [, setEmergencyDischargeTokens] = useState(0);
  const [dischargeForms, setDischargeForms] = useState(null);
  const [, setDischargeFormsTokens] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [, setAlertsTokens] = useState(0);
  const [userQuestion, setUserQuestion] = useState("");
  const [response, setResponse] = useState("");
  const [fetchingResponse, setFetchingResponse] = useState(false);

  const estimateTokens = (text) => {
    const words = text.split(/\s+/);
    const wordCount = words.length;
    const characterCount = text.length;

    const tokens = Math.ceil(characterCount / 4) + Math.ceil(wordCount / 0.75);
    return tokens;
  };

  const formatDate = (date) => {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    return date.toLocaleString("en-US", options).replace(",", " at");
  };

  const convertTimestamps = (data) => {
    if (Array.isArray(data)) {
      return data.map((item) => convertTimestamps(item));
    } else if (typeof data === "object" && data !== null) {
      const convertedObject = {};
      for (const key in data) {
        if (
          data[key] &&
          data[key].seconds !== undefined &&
          data[key].nanoseconds !== undefined
        ) {
          convertedObject[key] = formatDate(
            new Date(data[key].seconds * 1000 + data[key].nanoseconds / 1000000)
          );
        } else {
          convertedObject[key] = convertTimestamps(data[key]);
        }
      }
      return convertedObject;
    } else {
      return data;
    }
  };

  const openAiApiKey = process.env.REACT_APP_OPENAI_API_KEY;

  async function getOpenAIResponse(prompt) {
    try {
      const response = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        {
          // model: "gpt-4o",
          model: "gpt-3.5-turbo",
          messages: [
            { role: "system", content: "You are a helpful assistant." },
            { role: "user", content: prompt },
          ],
          max_tokens: 2048,
        },
        {
          headers: {
            Authorization: `Bearer ${openAiApiKey}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data.choices[0].message.content;
    } catch (error) {
      console.error("Error fetching OpenAI response:", error); // Log the error
      if (error.response) {
        // The request was made and the server responded with a status code that falls out of the range of 2xx
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
        console.error("Response headers:", error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        console.error("Request data:", error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error("Error message:", error.message);
      }
    }
  }

  const fetchClientData = async (clientId) => {
    console.log("Setting loading to true");
    setLoading(true);
    setProgress(10);
    try {
      const clientDoc = await getDoc(doc(db, "clients", clientId));
      console.log("Client Document:", clientDoc.data());
      setProgress(20);
      const preAdmissionDoc = await getDoc(
        doc(db, "preAdmissionAssessments", clientId)
      );
      console.log("Pre Admission Document:", preAdmissionDoc.data());
      setProgress(30);

      let clientData = {};
      if (clientDoc.exists()) {
        clientData = { ...clientData, ...clientDoc.data() };
      }
      if (preAdmissionDoc.exists()) {
        clientData = { ...clientData, ...preAdmissionDoc.data() };
      }

      const {
        profilePicture,
        fileUrls,
        lastName,
        searchTerms,
        personalEmail,
        fileUrlsRoi,
        filesUrlPreAdm,

        ...filteredClientData
      } = clientData;

      const convertedClientData = convertTimestamps(filteredClientData); // Utiliser les données filtrées ici
      setClientInfo(convertedClientData);
      console.log("Client Info after filtering:", convertedClientData);
      setProgress(40);

      const clientDataText = JSON.stringify(filteredClientData); // Utiliser les données filtrées ici
      const clientDataTokensCount = estimateTokens(clientDataText);
      setClientInfoTokens(clientDataTokensCount);

      const notesQuery = query(
        collection(db, "notes"),
        where("clientId", "==", clientId)
      );
      const notesSnapshot = await getDocs(notesQuery);
      setProgress(50);
      const notes = [];
      notesSnapshot.forEach((doc) => {
        notes.push(doc.data());
      });

      const convertedNotes = convertTimestamps(notes).map((note) => {
        const {
          clientLastName,
          clientProfilePicture,
          fileUrls,
          ...filteredNote
        } = note;
        return filteredNote;
      });
      setClientNotes(convertedNotes);
      console.log("Client Notes:", convertedNotes);

      const notesText = convertedNotes
        .map((note) => JSON.stringify(note))
        .join("\n");
      const notesTokensCount = estimateTokens(notesText);
      setClientNotesTokens(notesTokensCount);
      setProgress(60);

      const emergencyDischargeDoc = await getDoc(
        doc(db, "EmergencyDischarge", clientId)
      );

      let emergencyDischargeData = {};
      if (emergencyDischargeDoc.exists()) {
        emergencyDischargeData = emergencyDischargeDoc.data();
      }

      emergencyDischargeData = convertTimestamps(emergencyDischargeData);
      setEmergencyDischarge(emergencyDischargeData);
      console.log("Emergency Discharge Data:", emergencyDischargeData);
      const emergencyDischargeText = JSON.stringify(emergencyDischargeData);
      const emergencyDischargeTokensCount = estimateTokens(
        emergencyDischargeText
      );
      setEmergencyDischargeTokens(emergencyDischargeTokensCount);
      setProgress(70);

      const dischargeFormsDoc = await getDoc(
        doc(db, "dischargeForms", clientId)
      );
      let dischargeFormsData = {};
      if (dischargeFormsDoc.exists()) {
        dischargeFormsData = dischargeFormsDoc.data();
      }

      dischargeFormsData = convertTimestamps(dischargeFormsData);
      setDischargeForms(dischargeFormsData);
      console.log("Discharge Forms Data:", dischargeFormsData);
      const dischargeFormsText = JSON.stringify(dischargeFormsData);
      const dischargeFormsTokensCount = estimateTokens(dischargeFormsText);
      setDischargeFormsTokens(dischargeFormsTokensCount);
      setProgress(80);

      const alertsQuery = query(collection(db, "clients", clientId, "alerts"));
      const alertsSnapshot = await getDocs(alertsQuery);
      const alertsArray = [];
      alertsSnapshot.forEach((doc) => {
        alertsArray.push({ id: doc.id, ...doc.data() });
      });

      const convertedAlerts = convertTimestamps(alertsArray).map((alert) => {
        const { lastName, ...filteredAlert } = alert;
        return filteredAlert;
      });
      setAlerts(convertedAlerts);

      console.log("Alerts Data:", convertedAlerts);
      const alertsText = convertedAlerts
        .map((alert) => JSON.stringify(alert))
        .join("\n");
      const alertsTokensCount = estimateTokens(alertsText);
      setAlertsTokens(alertsTokensCount);
      setProgress(100);

      const totalTokens =
        clientDataTokensCount +
        notesTokensCount +
        emergencyDischargeTokensCount +
        dischargeFormsTokensCount +
        alertsTokensCount;

      console.log("Total Tokens for Client:", totalTokens);
      if (totalTokens > 128000) {
        console.warn("Total tokens exceed the limit for GPT-4o.");
      }
    } catch (error) {
      console.error("Error fetching client data: ", error);
    }
    setLoading(false);
  };

  const handleGenerateSummary = async () => {
    if (clientInfo && userQuestion) {
      console.log("Setting fetchingResponse to true");
      setFetchingResponse(true); // Démarrer l'indicateur de progression

      const notesSummary =
        clientNotes.length > 0
          ? clientNotes.map((note) => JSON.stringify(note, null, 2)).join("\n")
          : "No notes available.";

      const alertsSummary =
        alerts.length > 0
          ? alerts.map((alert) => JSON.stringify(alert, null, 2)).join("\n")
          : "No alerts available.";

      const emergencyDischargeSummary = emergencyDischarge
        ? JSON.stringify(emergencyDischarge, null, 2)
        : "No emergency discharge details available.";

      const dischargeFormsSummary = dischargeForms
        ? JSON.stringify(dischargeForms, null, 2)
        : "No discharge forms available.";

      const prompt = `
        Here are the client's details:
        ${JSON.stringify(clientInfo, null, 2)}
  
        Here are the latest notes for this client:
        ${notesSummary}
  
        Here are the alerts for this client:
        ${alertsSummary}
  
        Here are the emergency discharge details:
        ${emergencyDischargeSummary}
  
        Here are the discharge forms:
        ${dischargeFormsSummary}
  
        User's question: ${userQuestion}
      `;

      console.log("Prompt for OpenAI:", prompt); // Log prompt

      const aiResponse = await getOpenAIResponse(prompt);
      console.log("AI Response:", aiResponse);
      setResponse(aiResponse);
      console.log("Setting fetchingResponse to false");
      setFetchingResponse(false); // Arrêter l'indicateur de progression
    }
  };

  useEffect(() => {
    console.log("Fetching client data for ID:", clientID);
    fetchClientData(clientID);
  }, [clientID]);

  return (
    <div className="tab-container-white">
      <div
        className="tab-container-title"
        style={{ width: "100%", textAlign: "center" }}
      >
        Ask the AI any question to get comprehensive client details and
        insights.
      </div>

      <div
        style={{
          width: "100%",
          height: "10px",
          display: "flex",
          justifyContent: "center",
          alignContent: "center",
          alignItems: "center",
          alignSelf: "center",
          marginTop: "10px",
        }}
      >
        {loading && (
          <Box sx={{ width: "20%", height: "10px", mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" color="textSecondary">{`${Math.round(
              progress
            )}%`}</Typography>
          </Box>
        )}
      </div>

      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: "600px",
            paddingTop: "15px",
            paddingBottom: "20px",
            display: "flex",
            flexDirection: "column",
            alignContent: "center",
            alignItems: "center",
            alignSelf: "center",
          }}
        >
          <TextField
            fullWidth
            value={userQuestion}
            onChange={(e) => setUserQuestion(e.target.value)}
            placeholder="Ask your question here"
          />
          {/* <Button variant="contained" onClick={handleGenerateSummary}>
          Ask the question
        </Button> */}
          <div style={{ paddingTop: "20px" }}>
            <Button
              variant="contained"
              onClick={handleGenerateSummary}
              disabled={fetchingResponse}
            >
              {fetchingResponse ? (
                <>
                  <CircularProgress size={24} />
                  {console.log("Button is in loading state")}
                </>
              ) : (
                "Ask the question"
              )}
            </Button>
            {console.log("fetchingResponse state:", fetchingResponse)}
          </div>
        </div>
      </div>

      {response && (
        <div
          style={{
            width: "100%",
            // paddingLeft: "60px",
            // paddingRight: "60px",
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignContent: "center",
            alignItems: "center",
            alignSelf: "center",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              width: "100%",
              textAlign: "center",
              fontSize: "22px",
              fontWeight: "600",
              display: "flex",
              justifyContent: "center",
              alignContent: "center",
              alignItems: "center",
              alignSelf: "center",
            }}
          >
            Answer :
          </div>
          <div
            style={{
              width: "60%",
              // paddingLeft: "60px",
              // paddingRight: "60px",
              textAlign: "left",
              display: "flex",
              justifyContent: "start",
              alignContent: "flex-start",
              alignItems: "center",
              alignSelf: "center",
              borderRadius: "20px",
              marginTop: "20px",
              backgroundColor: "#e2edfe",
              marginBottom: "20px",
              paddingLeft: "8px",
              paddingRight: "8px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
              flexDirection: "column",
            }}
          >
            <ReactMarkdown>{response}</ReactMarkdown>
          </div>
        </div>
      )}
      {/* {response && (
        <div>
          <h2>Generated Response</h2>
          <ReactMarkdown>{animatedResponse}</ReactMarkdown>
        </div>
      )} */}
    </div>
  );
}

export default Ai;
